import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L, { Icon, LatLng } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Navigation, Clock, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DealerLocation, TechnicianRoute } from '../types';
import { useTheme } from '@/contexts/ThemeContext';

// Fix for default markers in react-leaflet (similar to RecallMap)
delete (L.Icon.Default.prototype as unknown as { _getIconUrl: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

interface RouteConfirmationMapProps {
  dealerLocations: DealerLocation[];
  routes: TechnicianRoute[];
  onRouteSelect?: (route: TechnicianRoute) => void;
  showRouteLines?: boolean;
  selectedRouteId?: string | undefined;
  baseLocation?: {
    lat: number;
    lng: number;
  } | null;
}

// Custom marker icons
const createDealerIcon = (recallCount: number, skillLevel: number) => {
  const colors = {
    1: '#ef4444', // High skill - red
    2: '#f59e0b', // Medium skill - amber
    3: '#10b981' // Low skill - green
  };

  const color = colors[skillLevel as keyof typeof colors] || '#6b7280';

  // Create a canvas element to draw the icon
  const canvas = document.createElement('canvas');
  canvas.width = 30;
  canvas.height = 30;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    // Draw circle background
    ctx.beginPath();
    ctx.arc(15, 15, 13, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw text
    ctx.fillStyle = 'white';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(recallCount.toString(), 15, 15);
  }

  return new Icon({
    iconUrl: canvas.toDataURL(),
    className: 'dealer-marker',
    iconSize: [30, 30],
    iconAnchor: [15, 15]
  });
};

/** Home / technician anchor — same warm neutral as RouteWithDirections home (not blue like manual) */
const TECHNICIAN_HOME_MARKER_FILL = '#44403c';

const createTechnicianIcon = () => {
  // Create a canvas element to draw the icon
  const canvas = document.createElement('canvas');
  canvas.width = 25;
  canvas.height = 25;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    // Draw circle background
    ctx.beginPath();
    ctx.arc(12, 12, 10, 0, 2 * Math.PI);
    ctx.fillStyle = TECHNICIAN_HOME_MARKER_FILL;
    ctx.fill();
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw text
    ctx.fillStyle = 'white';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('T', 12, 12);
  }

  return new Icon({
    iconUrl: canvas.toDataURL(),
    className: 'technician-marker',
    iconSize: [25, 25],
    iconAnchor: [12, 12]
  });
};

const TechnicianHomeBaseMarker: React.FC<{ route: TechnicianRoute }> = ({ route }) => {
  const hb = route.technician.home_base;
  if (
    !hb ||
    hb.latitude == null ||
    hb.longitude == null ||
    typeof hb.latitude !== 'number' ||
    typeof hb.longitude !== 'number'
  ) {
    return null;
  }
  return (
    <Marker position={[hb.latitude, hb.longitude]} icon={createTechnicianIcon()}>
      <Popup>
        <div className='p-2 min-w-[200px]'>
          <div className='flex items-center gap-2 mb-2'>
            <User className='h-4 w-4 text-neutral-600 dark:text-neutral-400' />
            <h3 className='font-semibold text-sm'>{route.technician.full_name}</h3>
          </div>
          <div className='space-y-1 text-xs text-gray-600'>
            <p>📞 {route.technician.phone_number}</p>
            <p>✉️ {route.technician.email}</p>
          </div>
          <div className='flex items-center gap-2 mt-2'>
            <Badge variant='outline' className='text-xs'>
              {route.stops.length} stops
            </Badge>
            <Badge
              variant='outline'
              className={`text-xs ${
                route.technician.skill_level === 1
                  ? 'border-red-500 text-red-700'
                  : route.technician.skill_level === 2
                    ? 'border-yellow-500 text-yellow-700'
                    : 'border-green-500 text-green-700'
              }`}
            >
              {route.technician.skill_level === 1
                ? 'High'
                : route.technician.skill_level === 2
                  ? 'Medium'
                  : 'Low'}{' '}
              Skill
            </Badge>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

/** Vehix / recall — brand primary (aligns with RouteWithDirections) */
const ROUTE_STOP_FILL_VEHIX = '#d4854a';

const createRouteStopIcon = (stopNumber: number, routeColor: string) => {
  // Create a canvas element to draw the icon
  const canvas = document.createElement('canvas');
  canvas.width = 25;
  canvas.height = 25;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    // Draw circle background
    ctx.beginPath();
    ctx.arc(12, 12, 10, 0, 2 * Math.PI);
    ctx.fillStyle = routeColor || ROUTE_STOP_FILL_VEHIX;
    ctx.fill();
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw text
    ctx.fillStyle = 'white';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(stopNumber.toString(), 12, 12);
  }

  return new Icon({
    iconUrl: canvas.toDataURL(),
    className: 'route-stop-marker',
    iconSize: [25, 25],
    iconAnchor: [12, 12]
  });
};

// Manual stop — brand accent (complementary sky; matches RouteWithDirections / globals --accent)
const MANUAL_STOP_FILL = '#0ea5e9';

const createManualStopIcon = (stopNumber: number) => {
  const canvas = document.createElement('canvas');
  canvas.width = 28;
  canvas.height = 28;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    ctx.beginPath();
    ctx.arc(14, 14, 12, 0, 2 * Math.PI);
    ctx.fillStyle = MANUAL_STOP_FILL;
    ctx.fill();
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw stop number
    ctx.fillStyle = 'white';
    ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(stopNumber.toString(), 14, 14);
  }

  return new Icon({
    iconUrl: canvas.toDataURL(),
    className: 'manual-stop-marker',
    iconSize: [28, 28],
    iconAnchor: [14, 14]
  });
};

// Map bounds calculator - fits map to show all stops and routes
const MapBoundsCalculator: React.FC<{
  dealerLocations: DealerLocation[];
  routes: TechnicianRoute[];
  baseLocation?: { lat: number; lng: number } | null;
}> = ({ dealerLocations, routes, baseLocation }) => {
  const map = useMap();

  useEffect(() => {
    const allPoints: LatLng[] = [];

    // Add base location if provided
    if (baseLocation) {
      allPoints.push(new LatLng(baseLocation.lat, baseLocation.lng));
    }

    // Add dealer locations with valid coordinates
    dealerLocations.forEach(dealer => {
      if (dealer.latitude && dealer.longitude) {
        allPoints.push(new LatLng(dealer.latitude, dealer.longitude));
      }
    });

    // Add technician home bases with valid coordinates
    routes.forEach(route => {
      if (
        route.technician.home_base &&
        route.technician.home_base.latitude &&
        route.technician.home_base.longitude
      ) {
        allPoints.push(
          new LatLng(route.technician.home_base.latitude, route.technician.home_base.longitude)
        );
      }
    });

    // Add all route stop locations
    routes.forEach(route => {
      route.stops.forEach(stop => {
        if (stop.dealer.latitude && stop.dealer.longitude) {
          allPoints.push(new LatLng(stop.dealer.latitude, stop.dealer.longitude));
        }
      });
    });

    if (allPoints.length > 0) {
      const bounds = L.latLngBounds(allPoints);
      if (bounds.isValid()) {
        // Fit bounds with padding to ensure all markers are visible
        map.fitBounds(bounds, {
          padding: [50, 50],
          maxZoom: 13 // Prevent zooming in too close if points are nearby
        });
      }
    }
  }, [dealerLocations, routes, baseLocation, map]);

  return null;
};

export const RouteConfirmationMap: React.FC<RouteConfirmationMapProps> = ({
  dealerLocations,
  routes,
  showRouteLines = true,
  selectedRouteId,
  baseLocation
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const { resolvedTheme } = useTheme();

  // Set dark mode background on Leaflet container - persistent monitoring
  useEffect(() => {
    if (!mapRef.current) return;

    const container = mapRef.current.getContainer();
    if (!container) return;

    // Get computed background color from CSS variable
    const root = document.documentElement;
    const bgValue = getComputedStyle(root).getPropertyValue('--background').trim();
    const bgColor = resolvedTheme === 'dark' ? `hsl(${bgValue})` : 'transparent';

    // Function to set background - more aggressive
    const setBackground = () => {
      if (container) {
        // Set on container
        container.style.setProperty('background-color', bgColor, 'important');
        container.style.setProperty('background', bgColor, 'important');

        // Also set on all child panes
        const panes = container.querySelectorAll('.leaflet-pane');
        panes.forEach((pane: Element) => {
          (pane as HTMLElement).style.setProperty('background-color', bgColor, 'important');
          (pane as HTMLElement).style.setProperty('background', bgColor, 'important');
        });
      }
    };

    // Set immediately
    setBackground();

    // Set up MutationObserver to watch for style changes
    const observer = new MutationObserver(() => {
      setBackground();
    });

    observer.observe(container, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      subtree: false
    });

    // Also set up interval as backup (check every 500ms)
    const interval = setInterval(setBackground, 500);

    // Listen to map events that might trigger style changes
    const map = mapRef.current;
    const handleMapEvent = () => {
      setTimeout(setBackground, 100);
    };

    map.on('tileload', handleMapEvent);
    map.on('moveend', handleMapEvent);
    map.on('zoomend', handleMapEvent);

    return () => {
      observer.disconnect();
      clearInterval(interval);
      map.off('tileload', handleMapEvent);
      map.off('moveend', handleMapEvent);
      map.off('zoomend', handleMapEvent);
    };
  }, [resolvedTheme]);

  const routeColors = [
    '#3b82f6', // Blue
    '#ef4444', // Red
    '#10b981', // Green
    '#f59e0b', // Amber
    '#8b5cf6', // Purple
    '#06b6d4', // Cyan
    '#f97316', // Orange
    '#84cc16' // Lime
  ];

  // Calculate center point that includes all locations
  const calculateMapCenter = (): [number, number] => {
    const allLats: number[] = [];
    const allLngs: number[] = [];

    // Add base location
    if (
      baseLocation &&
      typeof baseLocation.lat === 'number' &&
      typeof baseLocation.lng === 'number'
    ) {
      allLats.push(baseLocation.lat);
      allLngs.push(baseLocation.lng);
    }

    // Add dealer locations with valid coordinates
    dealerLocations.forEach(dealer => {
      if (
        dealer.latitude != null &&
        dealer.longitude != null &&
        typeof dealer.latitude === 'number' &&
        typeof dealer.longitude === 'number' &&
        !isNaN(dealer.latitude) &&
        !isNaN(dealer.longitude)
      ) {
        allLats.push(dealer.latitude);
        allLngs.push(dealer.longitude);
      }
    });

    // Add technician home bases with valid coordinates
    routes.forEach(route => {
      if (
        route.technician.home_base &&
        route.technician.home_base.latitude != null &&
        route.technician.home_base.longitude != null &&
        typeof route.technician.home_base.latitude === 'number' &&
        typeof route.technician.home_base.longitude === 'number' &&
        !isNaN(route.technician.home_base.latitude) &&
        !isNaN(route.technician.home_base.longitude)
      ) {
        allLats.push(route.technician.home_base.latitude);
        allLngs.push(route.technician.home_base.longitude);
      }

      // Add route stops with valid coordinates
      route.stops.forEach(stop => {
        if (
          stop.dealer.latitude != null &&
          stop.dealer.longitude != null &&
          typeof stop.dealer.latitude === 'number' &&
          typeof stop.dealer.longitude === 'number' &&
          !isNaN(stop.dealer.latitude) &&
          !isNaN(stop.dealer.longitude)
        ) {
          allLats.push(stop.dealer.latitude);
          allLngs.push(stop.dealer.longitude);
        }
      });
    });

    // Calculate center point
    if (allLats.length > 0 && allLngs.length > 0) {
      const avgLat = allLats.reduce((sum, lat) => sum + lat, 0) / allLats.length;
      const avgLng = allLngs.reduce((sum, lng) => sum + lng, 0) / allLngs.length;
      // Validate the calculated center is not NaN
      if (!isNaN(avgLat) && !isNaN(avgLng)) {
        return [avgLat, avgLng];
      }
    }

    // Fallback to Florida center
    return [27.9944024, -81.7602544];
  };

  const mapCenter = calculateMapCenter();

  // Show fallback if no data available
  if (dealerLocations.length === 0 && routes.length === 0) {
    return (
      <div className='h-full w-full flex items-center justify-center bg-muted/20'>
        <div className='text-center'>
          <MapPin className='h-12 w-12 text-muted-foreground/50 mx-auto mb-4' />
          <p className='text-sm text-muted-foreground'>No route data available</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className='h-full w-full bg-background dark:bg-background route-preview-map'
      style={{ position: 'relative' }}
    >
      <MapContainer
        center={mapCenter}
        zoom={10}
        className='h-full w-full'
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
        whenReady={() => {
          const map = mapRef.current;
          if (!map) return;
          const container = map.getContainer();
          if (container) {
            const root = document.documentElement;
            const bgValue = getComputedStyle(root).getPropertyValue('--background').trim();
            const bgColor = resolvedTheme === 'dark' ? `hsl(${bgValue})` : 'transparent';
            container.style.setProperty('background-color', bgColor, 'important');
            container.style.setProperty('background', bgColor, 'important');

            setTimeout(() => {
              container.style.setProperty('background-color', bgColor, 'important');
              container.style.setProperty('background', bgColor, 'important');
            }, 100);
          }
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />

        <MapBoundsCalculator
          dealerLocations={dealerLocations}
          routes={routes}
          baseLocation={baseLocation || null}
        />

        {/* Dealer Markers */}
        {dealerLocations.map(dealer => (
          <Marker
            key={`${dealer.id || dealer.name}-${dealer.city}-${dealer.latitude}-${dealer.longitude}`}
            position={[dealer.latitude, dealer.longitude]}
            icon={createDealerIcon(dealer.recall_count, dealer.skill_level)}
          >
            <Popup>
              <div className='p-2 min-w-[200px]'>
                <div className='flex items-center gap-2 mb-2'>
                  <MapPin className='h-4 w-4 text-blue-600' />
                  <h3 className='font-semibold text-sm'>{dealer.name}</h3>
                </div>
                <div className='space-y-1 text-xs text-gray-600'>
                  <p>{dealer.address}</p>
                  <p>
                    {dealer.city}, {dealer.region} {dealer.postal_code}
                  </p>
                  {dealer.phone && <p>📞 {dealer.phone}</p>}
                </div>
                <div className='flex items-center gap-2 mt-2'>
                  <Badge variant='outline' className='text-xs'>
                    {dealer.recall_count} recalls
                  </Badge>
                  <Badge
                    variant='outline'
                    className={`text-xs ${
                      dealer.skill_level === 1
                        ? 'border-red-500 text-red-700'
                        : dealer.skill_level === 2
                          ? 'border-yellow-500 text-yellow-700'
                          : 'border-green-500 text-green-700'
                    }`}
                  >
                    {dealer.skill_level === 1
                      ? 'High'
                      : dealer.skill_level === 2
                        ? 'Medium'
                        : 'Low'}{' '}
                    Skill
                  </Badge>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Route Lines and Stop Markers */}
        {showRouteLines &&
          routes.map((route, routeIndex) => {
            const routeColor = routeColors[routeIndex % routeColors.length];
            const isSelected = selectedRouteId === route.technician_id;

            // Create route waypoints
            const waypoints: LatLng[] = [];

            // Add technician home base if available with valid coordinates
            if (
              route.technician.home_base &&
              route.technician.home_base.latitude != null &&
              route.technician.home_base.longitude != null
            ) {
              waypoints.push(
                new LatLng(
                  route.technician.home_base.latitude,
                  route.technician.home_base.longitude
                )
              );
            }

            // Add dealer locations in stop order
            route.stops
              .sort((a, b) => a.stop_order - b.stop_order)
              .forEach(stop => {
                if (stop.dealer.latitude != null && stop.dealer.longitude != null) {
                  waypoints.push(new LatLng(stop.dealer.latitude, stop.dealer.longitude));
                }
              });

            return (
              <React.Fragment key={route.technician_id}>
                {/* Route Polyline */}
                {waypoints.length > 1 && (
                  <Polyline
                    positions={waypoints}
                    color={routeColor}
                    weight={isSelected ? 5 : 3}
                    opacity={isSelected ? 0.9 : 0.6}
                    dashArray={isSelected ? undefined : '5, 5'}
                  />
                )}

                <TechnicianHomeBaseMarker route={route} />

                {/* Route Stop Markers */}
                {route.stops
                  .sort((a, b) => a.stop_order - b.stop_order)
                  .filter(stop => stop.dealer.latitude != null && stop.dealer.longitude != null)
                  .map((stop, stopIndex) => {
                    const isManualStop = stop.stop_type === 'manual';
                    const manualStopReason = stop.recalls?.[0]?.manual_stop_reason;

                    return (
                      <Marker
                        key={[
                          route.technician_id,
                          stop.dealer.id || stop.dealer.name,
                          stop.dealer.city,
                          stopIndex
                        ].join('-')}
                        position={[stop.dealer.latitude, stop.dealer.longitude]}
                        icon={
                          isManualStop
                            ? createManualStopIcon(stopIndex + 1)
                            : createRouteStopIcon(stopIndex + 1, ROUTE_STOP_FILL_VEHIX)
                        }
                      >
                        <Popup>
                          <div className='p-2 min-w-[200px]'>
                            <div className='flex items-center gap-2 mb-2'>
                              <Navigation
                                className={`h-4 w-4 ${isManualStop ? 'text-brand-accent-600' : 'text-blue-600'}`}
                              />
                              <h3 className='font-semibold text-sm'>
                                Stop {stopIndex + 1}
                                {isManualStop && (
                                  <span className='ml-2 text-xs font-normal text-brand-accent-600'>
                                    (Manual)
                                  </span>
                                )}
                              </h3>
                            </div>
                            <div className='space-y-1 text-xs text-gray-600'>
                              <p>
                                <strong>{stop.dealer.name}</strong>
                              </p>
                              {isManualStop && manualStopReason && (
                                <p className='text-brand-accent-700 font-medium'>
                                  {manualStopReason}
                                </p>
                              )}
                              <p>{stop.dealer.address}</p>
                              <p>
                                {stop.dealer.city}, {stop.dealer.region}
                                {stop.dealer.postal_code ? ` ${stop.dealer.postal_code}` : ''}
                              </p>
                            </div>
                            <div className='flex items-center gap-2 mt-2'>
                              {isManualStop ? (
                                <Badge className='text-xs bg-brand-accent-500 text-white border-0'>
                                  MANUAL
                                </Badge>
                              ) : (
                                <Badge variant='outline' className='text-xs'>
                                  <Clock className='h-3 w-3 mr-1' />
                                  {stop.arrival_time}
                                </Badge>
                              )}
                              <Badge variant='outline' className='text-xs'>
                                {stop.service_duration} min
                              </Badge>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
              </React.Fragment>
            );
          })}
      </MapContainer>
    </div>
  );
};

export default RouteConfirmationMap;
